import React, { Component } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Users from '../../features/Users'
import LoginWindow from '../../containers/LoginWindow'
import styles from './Dashboard.scss'

class UserView extends Component {
    constructor(props) {
        super(props)

        // Initialize the state for the main section
        this.state = {
            hideSpinner: !this.props.showLogin,
            ssoOnline: true,
        }

        this.ssoRetry = undefined
    }

    componentDidMount() {
        this.ssoStatusCheck()
    }

    shouldComponentUpdate(nextProps, nextState) {
        const showLogin = nextProps.userCount <= 0
        if (showLogin && !nextProps.showLogin) {
            // console.log('showlogin changed', nextProps.userCount, nextProps.showLogin)
            return true
        }
        if (nextProps.userCount !== this.props.userCount) {
            // console.log('userCount changed', this.props.userCount, nextProps.userCount)
            return true
        }
        if (nextProps.doSteamLogin !== this.props.doSteamLogin) {
            // console.log('doSteamLogin changed', this.props.doSteamLogin, nextProps.doSteamLogin)
            return true
        }
        if (nextProps.showLogin !== this.props.showLogin) {
            // console.log('showLogin changed', this.props.showLogin, nextProps.showLogin)
            return true
        }
        if (nextProps.setLoginShowState !== this.props.setLoginShowState) {
            // console.log(
            //     'setLoginShowState changed',
            //     this.props.setLoginShowState,
            //     nextProps.setLoginShowState,
            // )
            return true
        }
        if (nextProps.logo !== this.props.logo) return true

        if (this.state.hideSpinner !== nextState.hideSpinner) {
            // console.log('hideSpinner changed', this.state.hideSpinner, nextState.hideSpinner)
            return true
        }
        if (this.state.ssoOnline !== nextState.ssoOnline) {
            // console.log('ssoOnline changed', this.state.ssoOnline, nextState.ssoOnline)
            return true
        }
        if (nextProps.readyForDisplay !== this.props.readyForDisplay) return true
        return false
    }

    componentDidUpdate(prevProps) {
        // Show login when there are no users logged in or we are doing a steam login
        const showLogin = this.props.userCount <= 0 || this.props.doSteamLogin

        if (prevProps.userCount !== this.props.userCount) {
            // Log entry that shows up when we're logging in a steam user
            if (this.props.doSteamLogin) console.info('Logging in steam user')

            this.toggleUserlistLogin(showLogin)
        } else if (showLogin && !this.props.showLogin) {
            this.toggleUserlistLogin(showLogin)
        }
    }

    componentWillUnmount() {
        if (this.ssoRetry !== undefined) {
            // When unmounting we need to make
            // sure to clear the retry interval
            clearInterval(this.ssoRetry)
            this.ssoRetry = undefined
        }
    }

    // Used to hide the loading spinner
    hideSpinner(value) {
        // If a component is ready, we hide the spinner
        if (typeof value === 'string' && value === 'ready') {
            this.setState({ hideSpinner: true })
        } else if (typeof value === 'string' && value === 'loading') {
            this.setState({ hideSpinner: false })
        }
    }

    // Used to toggle user list or login
    toggleUserlistLogin(showLogin) {
        this.props.setLoginShowState(showLogin)
        this.setState({ hideSpinner: !showLogin })
    }

    ssoStatusCheck = (server) => {
        // An option to disable SSO status checks altogether
        if (this.props.ssoStatus.url === 'undefined' || server === undefined) return

        // Generate the source link
        const source = this.props.ssoStatus.url.replace('{host}', server.sso.url)

        // Notify that we're starting the status checks
        // IFF this line is repeated for every check, the SSO iframe is having
        // troubles loading the SSO itself
        console.info(`Starting SSO status check for ${server.name} at ${source}`)

        // Display the offline message
        this.setState({ ssoOnline: false })

        // Start running the status checks
        this.runStatusCheck(source)
    }

    runStatusCheck = (source) => {
        // Gather the retry seconds from the attribute file
        const retrySeconds = this.props.ssoStatus.retrySeconds

        // Run get with axios on the provided source
        axios
            .get(source)
            .then((response) => {
                // We received a response from the SSO

                console.info(`SSO check responded with ${response.statusText}. Stopping retries.`)

                // Change the state to indicate that the SSO is online
                this.setState({ ssoOnline: true })

                // Clear theinterval and reset the variable
                clearInterval(this.ssoRetry)
                this.ssoRetry = undefined
            })
            .catch((error) => {
                // No response from the SSO

                // Figure out what kind of error we're dealing with
                if (error.response == null) {
                    // No response, most commonly the "Connection Refused" error
                    console.info(
                        `SSO status check received connection refused from ${source}. Retrying in ${retrySeconds} seconds.`,
                    )
                } else {
                    // Get the status text from the response
                    const status = error.response.statusText

                    // Log the status we got from the SSO
                    console.info(
                        `SSO check responded with an invalid status ${status} at ${source}. Retrying in ${retrySeconds} seconds.`,
                    )
                }

                // If we haven't initialized the retries
                // we initialize them now
                if (this.ssoRetry == null) {
                    // Create an interval for calling this same function
                    // with the retry second value provided in the settings
                    this.ssoRetry = setInterval(() => {
                        this.runStatusCheck(source)
                    }, 1000 * retrySeconds)
                }
            })
    }

    renderOfflineMessage = () => (
        // If the SSO is found as offline we display a message about the service
        <div className={styles.offlineMessage}>
            <h3>{this.props.offlineTexts.header}</h3>
            <p>{this.props.offlineTexts.message}</p>
            <p>{this.props.offlineTexts.retry}</p>
        </div>
    )

    renderSpinner = () => {
        if (!this.state.hideSpinner) {
            // Render the spinner
            return (
                <div className={styles.spinner}>
                    <div>
                        <FontAwesomeIcon icon="cog" size="3x" spin />
                        <p>{this.props.loadingText}</p>
                    </div>
                </div>
            )
        }
        return null
    }

    renderOverlay = () => {
        if (this.props.userCount > 0 && !this.state.hideSpinner) {
            // Render the overlay
            return <div className={styles.overlay} />
        }
        return null
    }

    // Used to render the user list
    renderUserList = () => {
        if (this.props.readyForDisplay && this.props.userCount > 0) {
            if (!this.props.showLogin || !this.state.hideSpinner) {
                return (
                    // Render the user list
                    <div className={styles.user_list}>
                        <Users
                            displayLoginWindow={showLogin => this.toggleUserlistLogin(showLogin)}
                        />
                    </div>
                )
            }
        }
        return null
    }

    // Used to render the login window
    renderLoginWindow = () => {
        if (!this.props.showLogin) {
            return null
        }

        if (this.props.readyForDisplay && this.props.autoRunEnabled) {
            this.props.disableAutoRun()
        }

        // Generate the necessary classes
        const classes =
            !this.state.hideSpinner || !this.state.ssoOnline
                ? [styles.loginDialog, styles.hidden].join(' ')
                : styles.loginDialog

        // Render the login window
        return (
            <div className={classes}>
                <LoginWindow
                    onReady={value => this.hideSpinner(value)}
                    displayLoginWindow={showLogin => this.toggleUserlistLogin(showLogin)}
                    ssoStatusCheck={server => this.ssoStatusCheck(server)}
                />
            </div>
        )
    }

    renderChineseLogo() {
        if (!this.props.logo) return null

        return (
            <img
                src={this.props.logo}
                className={`${styles.large_logo} ${styles[this.props.locale]}`}
                alt=""
            />
        )
    }

    render() {
        const classes = `${styles.dashboard} ${styles.flex_item} ${styles[this.props.layout]}`
        if (!this.state.ssoOnline) {
            return <div className={classes}>{this.renderOfflineMessage()}</div>
        }

        const loginWindow = this.renderLoginWindow()
        const spinner = this.renderSpinner()
        const overlay = this.renderOverlay()
        const userList = this.renderUserList()
        return (
            <div className={classes}>
                {this.renderChineseLogo()}
                {loginWindow}
                {userList}
                {spinner}
                {overlay}
            </div>
        )
    }
}

UserView.propTypes = {
    readyForDisplay: PropTypes.bool,
    userCount: PropTypes.number,
    doSteamLogin: PropTypes.bool,
    showLogin: PropTypes.bool,
    setLoginShowState: PropTypes.func,
    ssoStatus: PropTypes.shape({
        url: PropTypes.string,
        retrySeconds: PropTypes.number,
    }),
    logo: PropTypes.string,
    locale: PropTypes.string,
    offlineTexts: PropTypes.shape({
        header: PropTypes.string,
        message: PropTypes.string,
        retry: PropTypes.string,
    }),
    loadingText: PropTypes.string,
    autoRunEnabled: PropTypes.bool,
    disableAutoRun: PropTypes.func,
    layout: PropTypes.string,
}

UserView.defaultProps = {
    userCount: 0,
}

export default UserView



// WEBPACK FOOTER //
// ./src/components/Dashboard/Dashboard.js